import { useState, useCallback, useRef, useEffect } from 'react';
import type { WorkspaceLock } from '../../../types/dataGrid';
import {
  acquireWorkspaceLock,
  releaseWorkspaceLock,
  refreshWorkspaceLock,
} from '../../../services/dataGridApi';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface LockState {
  lock: WorkspaceLock | null;
  error: string | null;
  acquiring: boolean;
}

export function useWorkspaceLock(workspaceId: string | null) {
  const [state, setState] = useState<LockState>({
    lock: null,
    error: null,
    acquiring: false,
  });

  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(async () => {
      if (!workspaceId) return;
      try {
        const refreshed = await refreshWorkspaceLock(workspaceId);
        setState((prev) => ({ ...prev, lock: refreshed }));
      } catch {
        // Lock may have expired — notify user
        setState((prev) => ({
          ...prev,
          lock: null,
          error: 'Lock expired. Your changes may not save.',
        }));
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [workspaceId]);

  const acquire = useCallback(async () => {
    if (!workspaceId) return false;
    setState((prev) => ({ ...prev, acquiring: true, error: null }));

    try {
      const lock = await acquireWorkspaceLock(workspaceId);
      setState({ lock, error: null, acquiring: false });
      startHeartbeat();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to acquire lock';
      setState({ lock: null, error: message, acquiring: false });
      return false;
    }
  }, [workspaceId, startHeartbeat]);

  const release = useCallback(async () => {
    if (!workspaceId) return;
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    try {
      await releaseWorkspaceLock(workspaceId);
    } catch {
      // Best effort — lock will expire via TTL anyway
    }
    setState({ lock: null, error: null, acquiring: false });
  }, [workspaceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      // Release lock on unmount if held
      if (workspaceId && state.lock) {
        releaseWorkspaceLock(workspaceId).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    lock: state.lock,
    error: state.error,
    acquiring: state.acquiring,
    isLocked: state.lock !== null,
    acquire,
    release,
  };
}
